import { useState, type FC } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import {
  Code as CodeIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { YamlPreviewCardProps } from './types';
import { useYamlPreviewData } from './useYamlPreviewData';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.success.light}08 0%, ${theme.palette.info.light}08 50%, ${theme.palette.background.paper} 100%)`,
  border: `1px solid ${theme.palette.success.light}30`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
    background: `linear-gradient(135deg, ${theme.palette.success.light}12 0%, ${theme.palette.info.light}12 50%, ${theme.palette.background.paper} 100%)`,
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.success.main}`,
}));

const SectionContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const YamlContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[900],
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[700]}`,
  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
  fontSize: '0.875rem',
  lineHeight: 1.6,
  color: theme.palette.grey[100],
  overflow: 'auto',
  maxHeight: '500px',
  position: 'relative',
  '&:hover .copy-button': {
    opacity: 1,
  },
}));

const CopyButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: theme.palette.grey[800],
  color: theme.palette.grey[300],
  opacity: 0,
  transition: 'all 0.2s ease-in-out',
  width: 32,
  height: 32,
  '&:hover': {
    backgroundColor: theme.palette.grey[700],
    color: theme.palette.success.main,
  },
}));

const YamlContent = styled(Box)(({ theme }) => ({
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  '& .yaml-key': {
    color: theme.palette.info.light,
    fontWeight: 600,
  },
  '& .yaml-string': {
    color: theme.palette.success.light,
  },
  '& .yaml-value': {
    color: theme.palette.warning.light,
  },
  '& .yaml-comment': {
    color: theme.palette.grey[500],
    fontStyle: 'italic',
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  border: `2px dashed ${theme.palette.divider}`,
}));

export const YamlPreviewCard: FC<YamlPreviewCardProps> = ({
  selectedRepository,
  infrastructureDependencies = [],
  providesApis = [],
  componentInfoData,
  sechubData,
  blackduckData,
  sonarqubeData,
  pagerdutyData,
}) => {
  const { yamlString } = useYamlPreviewData({
    repositoryValue: selectedRepository,
    infrastructureDependencies,
    providesApis,
    sharedComponentData: componentInfoData,
    sharedSechubData: sechubData,
    sharedBlackduckData: blackduckData,
    sharedSonarqubeData: sonarqubeData,
    sharedPagerdutyData: pagerdutyData,
  });
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyError, setCopyError] = useState(false);

  // Don't render if no repository is selected
  if (!selectedRepository) {
    return null;
  }

  const handleCopyToClipboard = async () => {
    // Use promise-based API to ensure the rejection is handled explicitly
    window.navigator.clipboard
      .writeText(yamlString)
      .then(() => setCopySuccess(true))
      .catch(() => setCopyError(true));
  };

  const handleCloseCopySuccess = () => {
    setCopySuccess(false);
  };

  const handleCloseCopyError = () => {
    setCopyError(false);
  };

  return (
    <Fade in timeout={600}>
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <CardHeader>
            <CodeIcon color="success" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} color="success">
              Catalog Entity Preview
            </Typography>
          </CardHeader>

          <SectionContainer elevation={0}>
            <SectionHeader>
              <VisibilityIcon color="action" />
              <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                Generated YAML
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Read-only preview of the catalog entity
              </Typography>
            </SectionHeader>

            {yamlString ? (
              <YamlContainer>
                <CopyButton
                  className="copy-button"
                  size="small"
                  onClick={handleCopyToClipboard}
                  title="Copy to clipboard"
                >
                  <ContentCopyIcon fontSize="small" />
                </CopyButton>
                <YamlContent>{yamlString}</YamlContent>
              </YamlContainer>
            ) : (
              <EmptyState>
                <CodeIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  No YAML Preview Available
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Select a repository and configure integration settings to see
                  the catalog entity preview
                </Typography>
              </EmptyState>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CodeIcon color="info" />
              <Typography variant="body2" fontWeight={500}>
                <strong>Note:</strong> This preview shows how your catalog
                entity will appear in ODP.
              </Typography>
            </Box>
          </SectionContainer>

          <Snackbar
            open={copySuccess}
            autoHideDuration={2000}
            onClose={handleCloseCopySuccess}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={handleCloseCopySuccess}
              severity="success"
              variant="filled"
              sx={{ width: '100%' }}
            >
              YAML copied to clipboard!
            </Alert>
          </Snackbar>
          <Snackbar
            open={copyError}
            autoHideDuration={3000}
            onClose={handleCloseCopyError}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={handleCloseCopyError}
              severity="error"
              variant="filled"
              sx={{ width: '100%' }}
            >
              Failed to copy YAML to clipboard.
            </Alert>
          </Snackbar>
        </CardContent>
      </StyledCard>
    </Fade>
  );
};
